/**
 * LevelChipsRow Component
 * 
 * Horizontal scrollable row of academic level chips with:
 * - Left/right arrow buttons in fixed lanes (no overlap with chips)
 * - No glow, no shadow, no gradients
 * - Correct RTL/LTR scroll normalization
 * - Dynamic arrow visibility based on scroll position
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useStudentHub } from '../../contexts/StudentHubContext';
import './LevelChipsRow.css';

export default function LevelChipsRow() {
  const { t, i18n } = useTranslation();
  const { 
    academicLevel, 
    updateAcademicLevel, 
    ACADEMIC_LEVELS,
    levelLoading 
  } = useStudentHub();
  
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || document.documentElement.dir === 'rtl' || isArabic;
  const dir = isRTL ? 'rtl' : 'ltr';
  
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  
  // Normalize scrollLeft for RTL (some browsers use negative values)
  const normalize = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 0) {
      setCanPrev(false);
      setCanNext(false);
      return;
    }
    
    // Normalize scrollLeft for RTL
    const left = isRTL ? Math.abs(el.scrollLeft) : el.scrollLeft;
    
    setCanPrev(left > 2);
    setCanNext(left < max - 2);
  }, [isRTL]);
  
  // Listen to scroll and resize
  useEffect(() => {
    normalize();
    const el = scrollerRef.current;
    if (!el) return;
    
    el.addEventListener('scroll', normalize, { passive: true });
    window.addEventListener('resize', normalize);
    
    // Initial check after render
    const timer = setTimeout(normalize, 100);
    
    return () => {
      el.removeEventListener('scroll', normalize);
      window.removeEventListener('resize', normalize);
      clearTimeout(timer);
    };
  }, [normalize]);
  
  // Scroll by amount
  const scrollBy = (px) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: px, behavior: 'smooth' });
  };
  
  // Scroll delta - in RTL, visual "next" is negative scrollLeft
  const DELTA = 200;
  const prevDelta = isRTL ? DELTA : -DELTA;
  const nextDelta = isRTL ? -DELTA : DELTA;
  
  // Arrow glyphs - visual arrows, not logical
  const prevGlyph = isRTL ? '›' : '‹';
  const nextGlyph = isRTL ? '‹' : '›';
  
  // Handle level selection
  const handleLevelClick = async (levelKey) => {
    if (levelLoading) return;
    const level = levelKey === 'all' ? '' : levelKey;
    await updateAcademicLevel(level);
  };
  
  // All levels option + academic levels
  const allLevels = [
    { key: 'all', label_en: 'All Levels', label_ar: 'جميع المراحل' },
    ...ACADEMIC_LEVELS,
  ];
  
  return (
    <div className="levelWrap" dir={dir} data-testid="level-chips-row">
      {/* Prev Arrow - Fixed lane */}
      <button
        type="button"
        className="levelArrow levelArrowPrev"
        aria-label={t('lcr.scrollPrevious')}
        onClick={() => scrollBy(prevDelta)}
        style={{ visibility: canPrev ? 'visible' : 'hidden' }}
        data-testid="level-arrow-prev"
      >
        {prevGlyph}
      </button>
      
      {/* Scrollable Chips */}
      <div 
        ref={scrollerRef} 
        className="levelScroller" 
        role="tablist" 
        aria-label={t('lcr.academicLevels')}
      >
        {allLevels.map((level) => {
          const isActive = academicLevel === level.key || 
            (level.key === 'all' && (!academicLevel || academicLevel === ''));
          
          return (
            <button
              key={level.key}
              type="button"
              className={`levelChip ${isActive ? 'active' : ''} ${levelLoading ? 'loading' : ''}`}
              onClick={() => handleLevelClick(level.key)}
              role="tab"
              aria-selected={isActive}
              disabled={levelLoading}
              data-testid={`level-chip-${level.key}`}
            >
              {isRTL ? level.label_ar : level.label_en}
            </button>
          );
        })}
      </div>
      
      {/* Next Arrow - Fixed lane */}
      <button
        type="button"
        className="levelArrow levelArrowNext"
        aria-label={t('lcr.scrollNext')}
        onClick={() => scrollBy(nextDelta)}
        style={{ visibility: canNext ? 'visible' : 'hidden' }}
        data-testid="level-arrow-next"
      >
        {nextGlyph}
      </button>
    </div>
  );
}
